import React, {useMemo} from 'react';
import {getRandomHexColour, getTextColorForHexCode} from "../helpers/Colour";
import Icon from "../components/Icons/Icon";
import {pageValueIsSet, renderPageValue} from "../helpers/Page";
import {
    PROMO_CTA,
    PROMO_DESCRIPTION,
    PROMO_PRIMARY_HERO,
    PROMO_SECONDARY_HERO,
    promoSectionIsEnabled
} from "../helpers/Promo";
import {scrollToElement} from "../helpers/Animation";

const calculateLazyImagesToLoad = () => {
    let screenWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

    if (screenWidth >= 1390) {
        return 12;
    } else if (screenWidth >= 1200) {
        return 16;
    } else if (screenWidth >= 1045) {
        return 9;
    } else if (screenWidth >= 768) {
        return 12;
    } else if (screenWidth >= 666) {
        return 6;
    }

    return 8;
}

let NUMBER_BEFORE_LAZY_IMAGES = calculateLazyImagesToLoad();

const AlbumGallerySection = (props) => {
    const {
        page,
        albums
    } = props;

    const promoSectionEnabled = useMemo(() => {
        return promoSectionIsEnabled(page)
    }, [page]);

    const getTempAlbumCover = () => {
        let tempBackground = (getRandomHexColour());
        let tempOverlay = getTextColorForHexCode(tempBackground);

        return "https://placehold.co/800x800/"+tempBackground+"/"+tempOverlay+"?text=.";
    }

    const promoNavigate = () => {
        scrollToElement('#contact-section', () => {});
    }

    const renderTile = (index, album) => {
        let coverUrl = album?.album_cover?.url;
        if (!coverUrl) {
            coverUrl = getTempAlbumCover();
        }

        let alt = album?.title;
        if (!alt) {
            alt = album?.artist;
        } else if (!!album.artist) {
            alt = album.title+' - '+album.artist;
        }

        if (!alt) {
            alt = 'Conah Tucker';
        }

        let icons = [];

        if (!!album.spotify) {
            icons.push({
                url:    album.spotify,
                icon:   'text-theme-black fab fa-spotify',
                title:  'Spotify',
            });
        }

        if (!!album.sound_cloud) {
            icons.push({
                url:    album.sound_cloud,
                icon:   'text-theme-black fab fa-soundcloud',
                title:  'Sound Cloud',
            });
        }

        if (!!album.band_camp) {
            icons.push({
                url:    album.band_camp,
                icon:   'text-theme-black fab fa-bandcamp',
                title:  'Band Camp',
            });
        }

        if (!!album.beat_port) {
            icons.push({
                url:    album.beat_port,
                icon:   'text-theme-black fas fa-chevron-circle-right',
                title:  'Beatport',
            });
        }

        return (
            <div className="col-6 col-md-4 col-xl-3" key={index}>
                <div className="album ratio ratio-1x1">
                    <img src={coverUrl} alt={alt} loading={NUMBER_BEFORE_LAZY_IMAGES < index + 1 ? 'lazy' : 'eager'}/>
                    <div className="overlay p-2 p-md-5 d-flex flex-column justify-content-center align-items-center">
                        {!!album.title && (
                            <div className={'h5 mb-3 text-center'}>{album.title}</div>
                        )}
                        {!!album.artist && (
                            <div className={'h5 m-0 text-center'}>{album.artist}</div>
                        )}
                        {!!album.label && (
                            <>
                                <div className={'h5 m-0 text-center'}>-</div>
                                <div className={'h5 m-0 text-center'}>{album.label}</div>
                            </>
                        )}
                        {!!icons && (
                            <div className={'d-flex align-items-center justify-center'}>
                                {icons.map((icon, index) => {
                                    return (
                                        <div
                                            key={index}
                                            className='d-flex justify-content-center align-items-center'
                                        >
                                            <a href={icon.url} className={'mt-1 p-1'} target={'_blank'}>
                                                <Icon
                                                    className={'fa-2x '+icon.icon}
                                                    title={icon.title}
                                                />
                                            </a>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div id="album-gallery-section">
            <div className="section-guttering-container">
                <div className="section-guttering-inner">
                    <div className="tag-line">
                        <div className={'w-100 d-flex justify-content-center align-items-center mb-4'}>
                            <h1 className="m-0 p-0 h3 text-theme-black text-center">{renderPageValue(page, 'title', 'Mastering Engineer.')}</h1>
                        </div>
                        <div className={'w-100 d-flex justify-content-center align-items-center'}>
                            <h2 className="m-0 p-0 h5 text-theme-black text-center">{renderPageValue(page, 'sub_title', 'Engineering confidence into your artistry.')}</h2>
                        </div>
                        {promoSectionEnabled && (
                            <div className={'w-100 d-flex mt-5 p-1 border border-black justify-content-center align-items-center'}>
                                <div className={'w-100 d-flex p-5 border border-black flex-column justify-content-center align-items-center'}>
                                    {(pageValueIsSet(page, PROMO_PRIMARY_HERO) || pageValueIsSet(page, PROMO_SECONDARY_HERO)) && (
                                        <div className={'mb-4'}>
                                            {pageValueIsSet(page, PROMO_PRIMARY_HERO) && (
                                                <h3 className={"m-0 p-0 h5 text-theme-black text-center "+(pageValueIsSet(page, PROMO_SECONDARY_HERO) ? ' mb-2' : '')}>
                                                    {renderPageValue(page, PROMO_PRIMARY_HERO, '')}
                                                </h3>
                                            )}
                                            {pageValueIsSet(page, PROMO_SECONDARY_HERO) && (
                                                <h4 className="m-0 p-0 h6 text-theme-black text-center">
                                                    {renderPageValue(page, PROMO_SECONDARY_HERO, '')}
                                                </h4>
                                            )}
                                        </div>
                                    )}
                                    {pageValueIsSet(page, PROMO_DESCRIPTION) && (
                                        <p className="m-0 mb-4 p-0 text-theme-black text-center">{renderPageValue(page, PROMO_DESCRIPTION, '')}</p>
                                    )}
                                    {pageValueIsSet(page, PROMO_CTA) && (
                                        <button
                                            className={"btn btn-sm btn-primary btn-theme-black py-1 px-4"}
                                            onClick={promoNavigate}
                                        >
                                            {renderPageValue(page, PROMO_CTA, 'View details')}
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="gallery row g-2">
                        {albums.map((album, index) => renderTile(index, album))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AlbumGallerySection;
